import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Alert, MenuItem, Chip,
  CircularProgress, TablePagination
} from '@mui/material';
import { Save, Assessment, CheckCircle, ArrowForward } from '@mui/icons-material';
import { salaryService, userService } from '../services/api';
import jsPDF from 'jspdf';
import { usePagination } from '../hooks/usePagination';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount);
}

export function generatePayslipPDF(payslip: any, monthLabel: string) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const col2 = pageWidth / 2 + 5;
  let y = 15;

  doc.setFillColor(25, 118, 210);
  doc.rect(0, 0, pageWidth, 32, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(payslip.companyName || 'Mqbit Technologies', pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('SALARY SLIP', pageWidth / 2, 20, { align: 'center' });
  doc.setFontSize(10);
  doc.text(monthLabel, pageWidth / 2, 27, { align: 'center' });

  y = 40;
  doc.setTextColor(0, 0, 0);

  // Employee details box
  doc.setFillColor(248, 249, 250);
  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, y, pageWidth - margin * 2, 28, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('EMPLOYEE DETAILS', margin + 3, y + 6);

  const empLeft = [
    ['Employee Name', payslip.userName || ''],
    ['Employee Code', payslip.employeeCode || 'N/A'],
  ];
  const empRight = [
    ['Designation', payslip.designation || 'N/A'],
    ['Department', payslip.department || 'N/A'],
  ];

  empLeft.forEach(([label, value], i) => {
    const ly = y + 13 + i * 7;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
    doc.text(`${label}:`, margin + 3, ly);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    doc.text(String(value), margin + 38, ly);
  });
  empRight.forEach(([label, value], i) => {
    const ly = y + 13 + i * 7;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(80, 80, 80);
    doc.text(`${label}:`, col2, ly);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    doc.text(String(value), col2 + 30, ly);
  });

  y += 35;

  // Attendance summary
  doc.setFillColor(232, 244, 253); doc.setDrawColor(200, 200, 200);
  doc.rect(margin, y, pageWidth - margin * 2, 20, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
  doc.text('ATTENDANCE SUMMARY', margin + 3, y + 6);

  const attItems = [
    { label: 'Working Days', value: String(payslip.workingDays ?? 0) },
    { label: 'Present Days', value: String(payslip.presentDays ?? 0) },
    { label: 'Paid Leave',   value: String(payslip.paidLeaveDays ?? 0) },
    { label: 'Absent Days',  value: String(payslip.absentDays ?? 0) },
    { label: 'LWP Days',     value: String(payslip.lwpDays ?? 0) },
  ];
  const cellW = (pageWidth - margin * 2) / attItems.length;
  attItems.forEach(({ label, value }, i) => {
    const cx = margin + i * cellW + cellW / 2;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(25, 118, 210);
    doc.text(value, cx, y + 13, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(80, 80, 80);
    doc.text(label, cx, y + 18, { align: 'center' });
  });

  y += 27;

  const halfW = (pageWidth - margin * 2 - 5) / 2;

  // Headers
  doc.setFillColor(25, 118, 210);
  doc.rect(margin, y, halfW, 8, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('EARNINGS', margin + halfW / 2, y + 5.5, { align: 'center' });

  doc.setFillColor(211, 47, 47);
  doc.rect(margin + halfW + 5, y, halfW, 8, 'F');
  doc.text('DEDUCTIONS', margin + halfW + 5 + halfW / 2, y + 5.5, { align: 'center' });
  y += 8;

  const perDay = Number(payslip.perDaySalary) || 0;
  const absentDed = perDay * (payslip.absentDays || 0);
  const lwpDed = Math.max(0, Number(payslip.lwpDeduction) - absentDed);
  const earnings    = [['Basic Salary', formatCurrency(payslip.monthlySalary)]];
  const deductions  = [
    ['Absent Deduction', formatCurrency(absentDed)],
    ['LWP Deduction',    formatCurrency(lwpDed)],
  ];

  const rowH = 8;
  const maxRows = Math.max(earnings.length, deductions.length);
  for (let i = 0; i < maxRows; i++) {
    const ry = y + i * rowH;
    const bg = i % 2 === 0 ? 250 : 245;
    doc.setFillColor(bg, bg, bg); doc.rect(margin, ry, halfW, rowH, 'F');
    doc.setDrawColor(220, 220, 220); doc.rect(margin, ry, halfW, rowH, 'D');
    if (earnings[i]) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(0, 0, 0);
      doc.text(earnings[i][0], margin + 3, ry + 5.5);
      doc.setFont('helvetica', 'bold');
      doc.text(earnings[i][1], margin + halfW - 3, ry + 5.5, { align: 'right' });
    }
    doc.setFillColor(i % 2 === 0 ? 255 : 250, i % 2 === 0 ? 248 : 245, i % 2 === 0 ? 248 : 245);
    doc.rect(margin + halfW + 5, ry, halfW, rowH, 'F');
    doc.rect(margin + halfW + 5, ry, halfW, rowH, 'D');
    if (deductions[i]) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(0, 0, 0);
      doc.text(deductions[i][0], margin + halfW + 8, ry + 5.5);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(211, 47, 47);
      doc.text(deductions[i][1], margin + halfW + 5 + halfW - 3, ry + 5.5, { align: 'right' });
    }
  }
  y += maxRows * rowH;

  // Totals
  doc.setFillColor(232, 245, 233); doc.rect(margin, y, halfW, 9, 'F');
  doc.setDrawColor(200, 200, 200); doc.rect(margin, y, halfW, 9, 'D');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(27, 94, 32);
  doc.text('Total Earnings', margin + 3, y + 6);
  doc.text(formatCurrency(payslip.monthlySalary), margin + halfW - 3, y + 6, { align: 'right' });

  doc.setFillColor(255, 235, 238); doc.rect(margin + halfW + 5, y, halfW, 9, 'F');
  doc.rect(margin + halfW + 5, y, halfW, 9, 'D');
  doc.setTextColor(183, 28, 28);
  doc.text('Total Deductions', margin + halfW + 8, y + 6);
  doc.text(formatCurrency(payslip.lwpDeduction), margin + halfW + 5 + halfW - 3, y + 6, { align: 'right' });
  y += 17;

  // Net salary
  doc.setFillColor(25, 118, 210); doc.rect(margin, y, pageWidth - margin * 2, 16, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(255, 255, 255);
  doc.text('NET SALARY', margin + 5, y + 10);
  doc.text(formatCurrency(payslip.netSalary), pageWidth - margin - 5, y + 10, { align: 'right' });

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 12;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, footerY - 4, pageWidth - margin, footerY - 4);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120);
  doc.text('This is a system-generated payslip and does not require a signature.', pageWidth / 2, footerY, { align: 'center' });

  doc.save(`Payslip_${(payslip.userName || 'Employee').replace(/ /g, '_')}_${monthLabel.replace(/ /g, '_')}.pdf`);
}

// ─────────────────────────────────────────────────────────────────────────────

const SalaryReport: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const [previewData, setPreviewData] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [alert, setAlert] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    // Deactivated employees don't show up in the regular Employee filter here.
    userService.getUsers().then((data: any[]) => setUsers((data || []).filter(u => u.isActive))).catch(() => {});
  }, []);

  const showAlert = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 5000);
  };

  // Step 1: preview salary calculation
  const handleGenerate = async () => {
    setGenerating(true);
    setSaved(false);
    setPreviewData([]);
    try {
      const data = await salaryService.getMonthlySalaryReport(
        month, year, selectedUserId || undefined
      );
      setPreviewData(data);
      if (data.length === 0) showAlert('No active employees found.', 'info');
    } catch {
      showAlert('Failed to generate report. Please try again.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Step 2: save to DB
  const handleSavePayslips = async () => {
    setSaving(true);
    try {
      const res = await salaryService.saveMonthlySalary(
        month, year, selectedUserId || undefined
      );
      setSaved(true);
      showAlert(res.message || 'Payslips saved successfully!', 'success');
    } catch (err: any) {
      showAlert(err.response?.data?.message || 'Error saving payslips.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const { pagedRows: pagedPreviewData, paginationProps: previewPaginationProps } = usePagination(previewData);

  const totalGross = previewData.reduce((s, e) => s + e.monthlySalary, 0);
  const totalDed   = previewData.reduce((s, e) => s + e.totalDeduction, 0);
  const totalNet   = previewData.reduce((s, e) => s + e.netSalary, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Generate Monthly Payslips
      </Typography>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.msg}
        </Alert>
      )}

      {/* ── Step 1: Filters ───────────────────────────────────────── */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Step 1 — Select Month & Employee
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            select label="Month" value={month}
            onChange={e => setMonth(Number(e.target.value))}
            size="small" sx={{ minWidth: 140 }}
          >
            {MONTHS.map((m, i) => (
              <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Year" type="number" value={year}
            onChange={e => setYear(Number(e.target.value))}
            size="small" sx={{ width: 110 }}
          />

          <TextField
            select label="Employee" value={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value === '' ? '' : Number(e.target.value))}
            size="small" sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All Employees</MenuItem>
            {users.map(u => (
              <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained" size="large"
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <Assessment />}
            onClick={handleGenerate}
            disabled={generating}
            sx={{ px: 4 }}
          >
            {generating ? 'Calculating…' : 'Preview Salary'}
          </Button>
        </Box>
      </Paper>

      {/* ── Step 2: Preview table ─────────────────────────────────── */}
      {previewData.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Step 2 — Review &amp; Generate Payslips
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {MONTHS[month - 1]} {year} · {previewData.length} employee(s)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {saved && (
                <Chip icon={<CheckCircle />} label="Payslips Saved" color="success" variant="outlined" />
              )}
              <Button
                variant="contained" color="success" size="large"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                onClick={handleSavePayslips}
                disabled={saving || saved}
                sx={{ px: 4 }}
              >
                {saving ? 'Saving…' : saved ? 'Already Saved' : 'Generate & Save Payslips'}
              </Button>
            </Box>
          </Box>

          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {[
              { label: 'Employees', value: previewData.length, color: 'primary.main' },
              { label: 'Total Gross', value: formatCurrency(totalGross), color: 'text.primary' },
              { label: 'Total Deductions', value: formatCurrency(totalDed), color: 'error.main' },
              { label: 'Total Net Payable', value: formatCurrency(totalNet), color: 'success.main' },
            ].map(c => (
              <Paper key={c.label} variant="outlined" sx={{ px: 2.5, py: 1.5, flex: '1 1 160px' }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h6" fontWeight="bold" color={c.color}>{c.value}</Typography>
              </Paper>
            ))}
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Employee', 'Gross Salary', 'Working Days', 'Present', 'Paid Leave', 'Absent', 'LWP', 'Per Day', 'Deduction', 'Net Salary'].map(h => (
                    <TableCell key={h} align={h === 'Employee' ? 'left' : 'right'}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedPreviewData.map(emp => (
                  <TableRow key={emp.userId} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>{emp.userName}</TableCell>
                    <TableCell align="right">{formatCurrency(emp.monthlySalary)}</TableCell>
                    <TableCell align="right">{emp.workingDays}</TableCell>
                    <TableCell align="right">{emp.presentDays}</TableCell>
                    <TableCell align="right" sx={{ color: emp.paidLeaveDays > 0 ? 'info.main' : 'inherit' }}>
                      {emp.paidLeaveDays}
                    </TableCell>
                    <TableCell align="right" sx={{ color: emp.absentDays > 0 ? 'error.main' : 'inherit' }}>
                      {emp.absentDays}
                    </TableCell>
                    <TableCell align="right" sx={{ color: emp.lwpDays > 0 ? 'error.main' : 'inherit' }}>
                      {emp.lwpDays}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(emp.perDaySalary)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(emp.totalDeduction)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{formatCurrency(emp.netSalary)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {previewData.length > 0 && <TablePagination {...previewPaginationProps} />}
          </TableContainer>

          {!saved && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArrowForward color="warning" fontSize="small" />
              <Typography variant="body2" color="warning.dark">
                Review the figures above, then click <strong>"Generate &amp; Save Payslips"</strong> to save and make them available to employees.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SalaryReport;
